import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const stats = [
    { value: "500+", label: "Partner Institutions", icon: "Building2" },
    { value: "95%", label: "Implementation Success", icon: "TrendingUp" },
    { value: "40%", label: "Average Cost Reduction", icon: "DollarSign" },
    { value: "24/7", label: "Technical Support", icon: "Headphones" }
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-primary to-secondary overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 neural-network-bg"></div>
      <div className="absolute inset-0 code-rain opacity-30"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-accent/10 rounded-full blur-xl animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-cyber-purple/10 rounded-full blur-xl animate-pulse-slow"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium">
                <Icon name="Zap" size={16} className="mr-2" />
                Trusted by Fortune 500 Companies
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
                Transform Your
                <span className="gradient-text block">Institution</span>
                with AI & Robotics
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed">
                Empower your organization with cutting-edge AI and Robotics education solutions. 
                Join 500+ institutions already transforming their workforce with measurable results.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                className="morphing-cta text-white font-semibold"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Demo
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
              >
                Download ROI Guide
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="text-sm text-muted-foreground">Trusted by:</div>
              <div className="flex items-center gap-4">
                <div className="px-3 py-1 bg-card rounded-lg border border-border text-sm font-medium">
                  MIT
                </div>
                <div className="px-3 py-1 bg-card rounded-lg border border-border text-sm font-medium">
                  Stanford
                </div>
                <div className="px-3 py-1 bg-card rounded-lg border border-border text-sm font-medium">
                  Google
                </div>
                <div className="px-3 py-1 bg-card rounded-lg border border-border text-sm font-medium">
                  Microsoft
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Interactive Demo Preview */}
          <div className="relative">
            <div className="holographic-card p-8 bg-card/50 backdrop-blur-md">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Live Demo Preview</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    <span className="text-sm text-success">Live</span>
                  </div>
                </div>
                
                <div className="aspect-video bg-background rounded-lg border border-border overflow-hidden">
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-accent to-cyber-purple rounded-full flex items-center justify-center mx-auto">
                        <Icon name="Play" size={24} color="white" />
                      </div>
                      <p className="text-muted-foreground">Interactive Lab Environment</p>
                      <Button variant="ghost" size="sm">
                        Start Virtual Tour
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-background/50 rounded-lg">
                    <div className="text-2xl font-bold text-accent">15min</div>
                    <div className="text-sm text-muted-foreground">Setup Time</div>
                  </div>
                  <div className="text-center p-3 bg-background/50 rounded-lg">
                    <div className="text-2xl font-bold text-success">99.9%</div>
                    <div className="text-sm text-muted-foreground">Uptime</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-accent/20 to-cyber-purple/20 rounded-lg flex items-center justify-center mx-auto">
                <Icon name={stat.icon} size={24} className="text-accent" />
              </div>
              <div className="text-3xl font-bold text-foreground">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;